import { TOKENS } from "~/constants";
import { useDagProgram } from "./useDagProgram";
import { BN } from "@coral-xyz/anchor";
import { PublicKey } from "@solana/web3.js";
import { getAssociatedTokenAddressSync, TOKEN_2022_PROGRAM_ID } from "@solana/spl-token";
import { useConnection } from "@solana/wallet-adapter-react";
import { lamportsToSol, uiAmountToLamports } from "~/utils/conversion";
import { BONDING_CURVE, calculateAveragePrice, calculatePriceFromX, calculateTokenAmountForRings } from "~/utils/bondingCurve";
import useSWR from "swr";

export const useQuote = ({ payToken, receiveToken, uiAmount }: { payToken: typeof TOKENS[keyof typeof TOKENS], receiveToken: typeof TOKENS[keyof typeof TOKENS], uiAmount: BN }) => {
  const { getProjectPda } = useDagProgram();
  const { connection } = useConnection()

  const fetch = async () => {
    const token = payToken.address === TOKENS.PLATFORM.address ? receiveToken : payToken
    const tokenMintPublicKey = new PublicKey(token.address);
    const projectPda = getProjectPda(token.address);
    const tokenVaultAddress = getAssociatedTokenAddressSync(
      tokenMintPublicKey,
      projectPda,
      true,
      TOKEN_2022_PROGRAM_ID
    )
    const platformMintPublicKey = new PublicKey(TOKENS.PLATFORM.address)
    const platformVaultAddress = getAssociatedTokenAddressSync(
      platformMintPublicKey,
      projectPda,
      true,
      TOKEN_2022_PROGRAM_ID
    )

    const [tokenVaultInfo, platformVaultInfo] = await Promise.all([
      connection.getTokenAccountBalance(tokenVaultAddress, 'confirmed'),
      connection.getTokenAccountBalance(platformVaultAddress, 'confirmed')
    ])

    const tokenVaultBalance = tokenVaultInfo ? new BN(tokenVaultInfo.value.amount) : new BN(0)
    const platformVaultBalance = platformVaultInfo ? new BN(platformVaultInfo.value.amount) : new BN(0)

    const currentSold = BONDING_CURVE.TOTAL_SUPPLY_SCALED.sub(tokenVaultBalance)
    const tokenPrice = BONDING_CURVE.SCALE.mul(BONDING_CURVE.ADJUSTMENT_SCALE).div(calculatePriceFromX(currentSold)).toNumber()

    return {
      currentSold,
      tokenPrice
    }
  }

  const { data, isLoading, mutate } = useSWR(['quote', payToken, receiveToken, uiAmount], fetch, {
    refreshInterval: 1000 * 10,
  })

  const lamports = uiAmountToLamports(uiAmount, payToken.decimals)
  let receiveAmount = new BN(0)
  if (data) {
    if (payToken.address === TOKENS.PLATFORM.address) {
      receiveAmount = calculateTokenAmountForRings(data.currentSold, lamports).mul(BONDING_CURVE.ADJUSTMENT_SCALE)
    } else {
      const nextTokenPrice = lamportsToSol(calculateAveragePrice(data.currentSold, lamports, true), receiveToken.decimals)
      receiveAmount = lamports.mul(new BN(nextTokenPrice * 10 ** receiveToken.decimals)).div(BONDING_CURVE.SCALE.mul(BONDING_CURVE.ADJUSTMENT_SCALE))
    }
  }
  
  return {
    tokenPrice: data?.tokenPrice ?? 0,
    receiveAmount,
    isLoading,
    mutate
  }
}